export function formatTimestamps(timestamps: number[], locale?: string): string[] {
  if (!timestamps || timestamps.length === 0) {
    return []
  }

  const sorted = [...timestamps].sort((a, b) => a - b)
  const dates = sorted.map((ts) => new Date(ts))
  const timeSpan = sorted.length > 1 ? sorted[sorted.length - 1] - sorted[0] : 0
  const avgInterval = sorted.length > 1 ? timeSpan / (sorted.length - 1) : 0
  const isDailyPattern = checkDailyPattern(sorted)

  const rule = FORMAT_RULES.find((r) => r.test(timestamps, avgInterval, isDailyPattern, dates))

  if (!rule) {
    // Fallback if no rule matches (shouldn't happen with current rules)
    const fallbackOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
    return dates.map((date) => new Intl.DateTimeFormat(locale, fallbackOptions).format(date))
  }

  return dates.map((currentDate, index) => {
    return formatSingleTimestamp(currentDate, index, dates, rule, locale)
  })
}

// Time constants in milliseconds
const TIME = {
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
  MONTH: 30 * 24 * 60 * 60 * 1000
}

// Custom type definitions for better type safety
type NeighborDates = { prev?: Date; next?: Date }
type CustomFormatResult = { custom: string }
type FormatResult = Intl.DateTimeFormatOptions | CustomFormatResult

interface FormatRule {
  name: string
  test: (
    timestamps: number[],
    avgInterval?: number,
    isDailyPattern?: boolean,
    dates?: Date[]
  ) => boolean
  format: (date: Date, neighbors?: NeighborDates, locale?: string) => FormatResult
}

const FORMAT_RULES: FormatRule[] = [
  {
    name: 'single',
    test: (timestamps: number[]) => !timestamps || timestamps.length <= 1,
    format: () => ({ month: 'short', day: 'numeric' } as Intl.DateTimeFormatOptions)
  },
  {
    name: 'hourly',
    test: (_: number[], avgInterval?: number) =>
      typeof avgInterval === 'number' && avgInterval < TIME.DAY - TIME.HOUR,
    format: (date: Date, neighbors?: NeighborDates) => {
      const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }

      // Add date if crossing day boundary
      if (
        neighbors?.prev &&
        (neighbors.prev.getDate() !== date.getDate() ||
          neighbors.prev.getMonth() !== date.getMonth())
      ) {
        options.month = 'short'
        options.day = 'numeric'
      }

      return options
    }
  },
  {
    name: 'daily',
    test: (_: number[], avgInterval?: number, isDailyPattern?: boolean) =>
      (typeof avgInterval === 'number' && avgInterval < TIME.WEEK - TIME.DAY / 2) ||
      !!isDailyPattern,
    format: (date: Date, neighbors?: NeighborDates) => {
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        hour: undefined,
        minute: undefined,
        second: undefined
      }

      const currentUTCFullYear = date.getUTCFullYear()
      if (
        (neighbors?.prev && neighbors.prev.getUTCFullYear() !== currentUTCFullYear) ||
        (neighbors?.next && neighbors.next.getUTCFullYear() !== currentUTCFullYear)
      ) {
        options.year = 'numeric'
      }

      return options
    }
  },
  {
    name: 'weekly',
    test: (_: number[], avgInterval?: number) =>
      typeof avgInterval === 'number' && avgInterval < TIME.MONTH,
    format: (date: Date, neighbors?: NeighborDates, locale?: string): CustomFormatResult => {
      const weekNumber = getWeekNumber(date)
      const isSwedish = locale?.toLowerCase().startsWith('sv')
      const weekPrefix = isSwedish ? 'v' : 'w'

      let showMonth = false
      const currentWeekStart = getStartOfWeek(date)
      const currentWeekStartMonth = currentWeekStart.getMonth()

      if (neighbors?.prev) {
        const prevWeekStart = getStartOfWeek(neighbors.prev)
        if (prevWeekStart.getMonth() !== currentWeekStartMonth) {
          showMonth = true
        }
      }

      if (neighbors?.next) {
        const nextWeekStart = getStartOfWeek(neighbors.next)
        if (nextWeekStart.getMonth() !== currentWeekStartMonth) {
          showMonth = true
        }
      }

      if (date.getMonth() !== currentWeekStartMonth) {
        showMonth = true
      }

      const monthString = showMonth
        ? new Intl.DateTimeFormat(locale, { month: 'short' }).format(currentWeekStart)
        : ''

      if (monthString) {
        return { custom: `${weekPrefix}${weekNumber} ${monthString}` }
      } else {
        return { custom: `${weekPrefix}${weekNumber}` }
      }
    }
  },
  {
    name: 'yearly',
    test: (_: number[], __?: number, ___?: boolean, dates?: Date[]) => {
      if (!dates || dates.length < 3) return false

      const firstDate = dates[0]
      return dates.every(
        (d) => d.getMonth() === firstDate.getMonth() && d.getDate() === firstDate.getDate()
      )
    },
    format: () => ({ year: 'numeric' } as Intl.DateTimeFormatOptions)
  },
  {
    name: 'monthly',
    test: () => true,
    format: (date: Date, neighbors?: NeighborDates) => {
      const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }

      if (
        (neighbors?.prev && neighbors.prev.getFullYear() !== date.getFullYear()) ||
        (neighbors?.next && neighbors.next.getFullYear() !== date.getFullYear())
      ) {
        options.year = 'numeric'
      }

      return options
    }
  }
]

function formatSingleTimestamp(
  currentDate: Date,
  currentIndex: number,
  dates: Date[],
  rule: FormatRule,
  locale?: string
): string {
  const neighbors: NeighborDates = {
    prev: currentIndex > 0 ? dates[currentIndex - 1] : undefined,
    next: currentIndex < dates.length - 1 ? dates[currentIndex + 1] : undefined
  }

  const formatOptions = rule.format(currentDate, neighbors, locale)

  if ('custom' in formatOptions) {
    return formatOptions.custom
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(currentDate)
}

function checkDailyPattern(timestamps: number[]): boolean {
  if (timestamps.length < 3) return false

  let dayIntervalCount = 0
  for (let i = 1; i < timestamps.length; i++) {
    const diff = timestamps[i] - timestamps[i - 1]
    // Allow 20-28 hours as "daily"
    if (diff >= 0.8 * TIME.DAY && diff <= 1.2 * TIME.DAY) {
      dayIntervalCount++
    }
  }

  // Consider it a daily pattern if 60% of intervals are approximately 1 day
  return dayIntervalCount > (timestamps.length - 1) * 0.6
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
}

// Helper function to get the start of the ISO week (Monday)
function getStartOfWeek(date: Date): Date {
  const d = new Date(date) // Clone date to avoid modifying original
  const day = d.getDay() // 0 = Sun, 1 = Mon, ..., 6 = Sat
  // Adjust day to be 1 (Monday) to 7 (Sunday) for ISO 8601 week date system
  const isoDay = day === 0 ? 7 : day
  // Calculate difference to Monday (day 1)
  const diff = isoDay - 1
  // Subtract the difference in days to get to Monday
  d.setDate(d.getDate() - diff)
  // Set time to midnight to avoid DST issues affecting the date part
  d.setHours(0, 0, 0, 0)
  return d
}
